import ContentShell from "components/ContentShell";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Button, FormLabel } from "@mui/material";
import { DEFAULT_IMAGE_DIMENSIONS, ImageType } from "utils/imageConstants";
import configData from "config/config.json";
import { useSnackbar } from "hooks/useSnackbar";
import useStyles from "styles/banner/bannerEditor";
import { SimpleTextInput } from "components/FormTextInput";
import {
  createSplashScreen,
  getSplashScreenInfo,
  updateSplashScreen,
} from "services/splashScreenService";
import FilePicker from "components/FilePicker";
import * as Yup from "yup";
import {
  uploadImageV2,
  uploadContentTranslations,
} from "services/uploadService";
import { SimpleSelectInput } from "components/FormSelectInput";
import { SimpleMultiSelectInput } from "components/FormMultiSelectInput";
import BulkTranslationUpload from "components/BulkTranslationUpload";
import TargetedUsersUpload from "components/TargetedUsersUpload";
import TranslationButtons from "components/TranslationButtons";
import useOpenClose from "hooks/useOpenClose";
import ViewLanguageModal from "screens/modals/ViewLanguageModal";
import SimpleSwitch from "components/Switch";
import sessionHelperInstance from "services/sessionHelper";
import SearchableInput from "components/SearchableInput";

interface SplashScreenDetails {
  title: string;
  titleTrans: Record<string, any>;
  navigationPath: string;
  body: string;
  bodyTrans: Record<string, any>;
  fileType: string;
  isActive: boolean;
  isTitleBold: boolean;
  isBodyBold: boolean;
  operators: any[];
  bodyFontSize: any;
  titleFontSize: any;
  order: any;
  targetedUsers: string[];
  imageUrl?: string;
}

const _splashScreenDetails: SplashScreenDetails = {
  title: "",
  titleTrans: {},
  navigationPath: "STORE",
  body: "",
  bodyTrans: {},
  fileType: "",
  isActive: true,
  isTitleBold: false,
  isBodyBold: false,
  operators: [],
  bodyFontSize: null,
  titleFontSize: null,
  order: null,
  targetedUsers: [],
};

const navigationPathOptions = [
  { id: "STORE", label: "Store" },
  { id: "DIVISIONS", label: "Divisions" },
  { id: "VAULT", label: "Vault" },
  { id: "MY_TEAM", label: "My Team" },
  { id: "LEADERBOARD", label: "Leaderboard" },
  { id: "LEADERBOARD_2", label: "Leaderboard 2.0" },
  { id: "CHALLENGES", label: "Challenges" },
];

const splashScreenValidation = Yup.object().shape({
  title: Yup.string().required("Title is required"),
  navigationPath: Yup.string().required("Navigation Path is required"),
  body: Yup.string().required("Body is required"),
  order: Yup.number().nullable().positive().min(0),
});

const SplashScreenEditor = () => {
  const styles = useStyles();
  const location = useLocation();
  const { showSnackbar } = useSnackbar();
  const id = location.pathname.split("/").slice(-1)[0];
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [splashScreenDetails, setSplashScreenDetails] =
    useState(_splashScreenDetails);
  const [image, setImage] = useState(null);
  const [imageDimensions, setImageDimensions] = useState<{
    targetWidth: number;
    targetHeight: number;
    originalWidth: number;
    originalHeight: number;
  } | null>(null);
  const [translationFile, setTranslationFile] = useState<File | null>(null);
  const { isOpen, close, open } = useOpenClose();
  const [modalData, setModalData] = useState({
    language: "",
    inputValue: "",
    field: null,
    viewData: [],
  });

  const cancel = () => navigate(configData.routes.splashScreen.splashScreen);

  const submit = async () => {
    try {
      setLoading(true);
      let response;
      await splashScreenValidation.validate(splashScreenDetails);

      if (id === "create") {
        response = await createSplashScreen({
          ...splashScreenDetails,
        });

        if (response?.data?.fileUploadUrl) {
          const fileUploadUrl = response?.data?.fileUploadUrl;
          await upload(fileUploadUrl);
        }

        if (translationFile && response?.data?._id) {
          await uploadContentTranslations(
            translationFile,
            "SPLASH_SCREEN",
            response.data._id,
          );
        }
      } else {
        const { fileType, imageUrl, ...rest } = splashScreenDetails;
        if (fileType !== "") rest["fileType"] = fileType;
        if (rest.order !== null && rest.order !== undefined) {
          rest.order = Number(rest.order);
        }

        response = await updateSplashScreen({ ...rest }, id);

        if (
          splashScreenDetails.fileType !== "" &&
          response?.data?.fileUploadUrl
        ) {
          const fileUploadUrl = response?.data?.fileUploadUrl;
          await upload(fileUploadUrl);
        }
      }
      showSnackbar(response.message, "success");
      setLoading(false);
      navigate(configData.routes.splashScreen.splashScreen);
    } catch (error) {
      if (error?.inner)
        error.inner.forEach((error) => showSnackbar(error.message, "error"));
      showSnackbar(error.message, "error");
      setLoading(false);
    }
  };

  const onChange = (e: any) => {
    const { name, value } = e?.target;
    let processedValue = value;

    // Convert numeric fields
    if (["bodyFontSize", "titleFontSize", "order"].includes(name)) {
      processedValue = value === "" ? null : Number(value);
    }

    let data = {
      ...splashScreenDetails,
      [name]: ["isActive", "isTitleBold", "isBodyBold"].includes(name)
        ? !splashScreenDetails[name]
        : processedValue,
    };
    if (["title", "body"].includes(name)) {
      data[`${name}Trans`]["en"] = value;
    }
    setSplashScreenDetails((state) => ({ ...state, ...data }));
  };

  const upload = async (url) => {
    try {
      const defaultDims = DEFAULT_IMAGE_DIMENSIONS[ImageType.SPLASH_SCREEN];
      const resp = await uploadImageV2({
        data: image,
        uploadUrl: url,
        resize: true,
        resizeToWidth: imageDimensions?.targetWidth || defaultDims.width,
        resizeToHeight: imageDimensions?.targetHeight || defaultDims.height,
      });

      if (resp.status) {
        showSnackbar(resp.message ?? "Image Uploaded", "success");
        return resp;
      } else {
        // Handle non-200 responses as errors
        throw new Error(
          resp.message || `Image upload failed with status ${resp.status}`,
        );
      }
    } catch (error) {
      showSnackbar(error.message || "Error uploading image!", "error");
      throw error; // Re-throw to let submit() know upload failed
    }
  };

  const onSelectImage = (image: any, dimensions?: any) => {
    setImage(image);
    if (dimensions) {
      setImageDimensions(dimensions);
    }
    setSplashScreenDetails((state) => ({
      ...state,
      fileType: "image/webp",
    }));
  };

  const fetchSplashScreenInfo = async () => {
    try {
      setLoading(true);
      const resp = await getSplashScreenInfo(id);
      if (resp?.data) {
        let titleTrans = { ...resp.data?.titleTrans };
        let bodyTrans = { ...resp.data?.bodyTrans };

        if (resp?.data?.body && !bodyTrans["en"]) {
          bodyTrans["en"] = resp.data?.body;
        } else resp.data.body = bodyTrans["en"];
        if (resp?.data?.title && !titleTrans["en"]) {
          titleTrans["en"] = resp.data?.title;
        } else resp.data.title = titleTrans["en"];

        // Ensure font sizes are numbers
        const processedData = {
          ...resp.data,
          bodyTrans,
          titleTrans,
          bodyFontSize: Number(resp.data?.bodyFontSize),
          titleFontSize: Number(resp.data?.titleFontSize),
          order: resp.data?.order != null ? Number(resp.data.order) : null,
          targetedUsers: resp.data?.targetedUsers || [],
        };

        setSplashScreenDetails(processedData);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      showSnackbar(error.message, "error");
    }
  };

  const getTranslatedData = (field: any) => {
    const _translatedData = Object.entries(splashScreenDetails?.[field]).map(
      ([key, value]) => ({
        lang: key,
        inputValue: value,
      }),
    );
    if (!_translatedData) return [];
    return _translatedData;
  };

  const handleOpenModal = (type, field) => {
    setModalData((state) => {
      if (type === "view") {
        let data = getTranslatedData(field);
        return { ...state, viewData: data, field, modal: type };
      } else {
        return {
          ...state,
          field,
          modal: type,
          language: "",
          inputValue: "",
        };
      }
    });
    open();
  };

  const handleChange = (event: any) => {
    const { name, value } = event.target;
    setModalData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleLangEditClick = (data: any) => {
    close();
    setModalData((state) => ({
      ...state,
      language: data?.lang,
      inputValue: data?.inputValue,
      modal: "add",
    }));
    open();
  };

  const handleLangDeleteClick = (data: any) => {
    if (data.lang === "en") {
      showSnackbar(
        "Cannot delete English translation, as it's a default language",
        "error",
      );
      return;
    }

    // remove from view data
    setModalData((state) => ({
      ...state,
      viewData: state.viewData.filter((item) => item.lang !== data.lang),
    }));

    setSplashScreenDetails((prev) => {
      prev[modalData.field] = Object.keys(prev[modalData.field]).reduce(
        (acc, key) => {
          if (key !== data.lang) {
            acc[key] = prev[modalData.field][key];
          }
          return acc;
        },
        {},
      );
      return { ...prev };
    });
  };

  const handleAddTranslation = () => {
    const { field } = modalData;
    if (!field) {
      showSnackbar("Confused which field to add the translation", "error");
      return;
    }

    setSplashScreenDetails((prev) => ({
      ...prev,
      [field]: {
        ...prev[field],
        [modalData.language]: modalData.inputValue,
      },
    }));

    // if english translation is available set it field
    if (modalData.language === "en" && modalData.inputValue !== "") {
      setSplashScreenDetails((details) => ({
        ...details,
        [field?.split("Trans")[0]]: modalData.inputValue,
        [field]: { ...details[field], en: modalData.inputValue },
      }));
    }
    setModalData((prev) => ({
      ...prev,
      inputValue: "",
      language: "",
      field: null,
    }));
    close();
  };

  const handleOperatorChange = (e: any) => {
    const { value } = e.target;
    const normalizedValue = Array.isArray(value)
      ? value.map((item: any) =>
          item && typeof item === "object" ? (item.id ?? item) : item,
        )
      : value && typeof value === "object"
        ? (value.id ?? value)
        : value;

    setSplashScreenDetails((prev) => ({
      ...prev,
      operators: Array.isArray(normalizedValue)
        ? normalizedValue
        : normalizedValue
          ? [normalizedValue]
          : [],
    }));
  };

  useEffect(() => {
    if (id !== "create") fetchSplashScreenInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ContentShell
      showBackButton
      onBackButtonClick={cancel}
      title={`${id === "create" ? "Create" : "Edit"} Splash Screen`}
      loading={loading}
      actions={
        <Box className={styles.buttonContainer}>
          <Button
            type="submit"
            variant="contained"
            style={{ backgroundColor: "red", color: "white" }}
            onClick={cancel}
          >
            Cancel
          </Button>
          <Button type="submit" variant="contained" onClick={submit}>
            Save
          </Button>
        </Box>
      }
    >
      <Box className={styles.container}>
        <Box className={styles.firstFormContainer}>
          <Box className={styles.field}>
            <SimpleTextInput
              label="Title"
              name="title"
              value={splashScreenDetails.title}
              onChange={onChange}
              button={
                <TranslationButtons
                  field={"titleTrans"}
                  onClick={handleOpenModal}
                />
              }
            />
          </Box>
          <Box className={styles.field}>
            <SimpleSelectInput
              label="Navigation Path"
              name="navigationPath"
              value={splashScreenDetails.navigationPath}
              options={navigationPathOptions}
              onChange={onChange}
            />
          </Box>
          <Box className={styles.field}>
            <SimpleTextInput
              label="Body"
              name="body"
              value={splashScreenDetails.body}
              onChange={onChange}
              button={
                <TranslationButtons
                  field={"bodyTrans"}
                  onClick={handleOpenModal}
                />
              }
            />
          </Box>
          <Box className={styles.field}>
            <SearchableInput
              selected={(splashScreenDetails.operators || []).map(
                (operatorId) => {
                  const operator = sessionHelperInstance.operatorOptions?.find(
                    (op) => op.id === operatorId,
                  );
                  return {
                    id: operatorId,
                    label: operator?.label || operatorId,
                  };
                },
              )}
              isMultiSelect={true}
              enableSelectAll={true}
              label={"Operators (Optional)"}
              name={"operators"}
              options={sessionHelperInstance.operatorOptions || []}
              onOptionSelect={handleOperatorChange}
              placeholder="Select operators (optional)"
              loading={loading}
            />
          </Box>{" "}
          <Box className={styles.field}>
            <SimpleTextInput
              label="Body Font Size"
              name="bodyFontSize"
              type="number"
              value={splashScreenDetails.bodyFontSize}
              onChange={onChange}
              placeholder="Enter Body Font Size"
            />
          </Box>{" "}
          <Box className={styles.field}>
            <SimpleTextInput
              label="Title Font Size"
              name="titleFontSize"
              type="number"
              value={splashScreenDetails.titleFontSize}
              onChange={onChange}
              placeholder="Enter Title Font Size"
            />
          </Box>
          <Box className={styles.field}>
            <SimpleTextInput
              label="Order (Optional)"
              name="order"
              type="number"
              value={splashScreenDetails.order}
              onChange={onChange}
              placeholder="Enter display order"
            />
          </Box>
          <Box className={styles.field} style={{ alignSelf: "center" }}>
            <SimpleSwitch
              legendText={"Splash Screen Status"}
              name="isActive"
              value={splashScreenDetails.isActive}
              label={splashScreenDetails.isActive ? "Active" : "Inactive"}
              onChange={onChange}
            />
          </Box>
          <Box className={styles.field} style={{ alignSelf: "center" }}>
            <SimpleSwitch
              legendText={"Title Style (Bold/Normal)"}
              name="isTitleBold"
              value={splashScreenDetails.isTitleBold}
              label={splashScreenDetails.isTitleBold ? "Bold" : "Normal"}
              onChange={onChange}
            />
          </Box>
          <Box className={styles.field} style={{ alignSelf: "center" }}>
            <SimpleSwitch
              legendText={"Body Style (Bold/Normal)"}
              name="isBodyBold"
              value={splashScreenDetails.isBodyBold}
              label={splashScreenDetails.isBodyBold ? "Bold" : "Normal"}
              onChange={onChange}
            />
          </Box>
        </Box>
        <Box className={styles.imagePickerContainer}>
          <FilePicker
            value={image}
            name="splashScreenImage"
            onFileSelect={onSelectImage}
            imageType={ImageType.SPLASH_SCREEN}
            enableDimensionControl={true}
            metaData={{
              note: `Recommended: ${
                DEFAULT_IMAGE_DIMENSIONS[ImageType.SPLASH_SCREEN].width
              } x ${
                DEFAULT_IMAGE_DIMENSIONS[ImageType.SPLASH_SCREEN].height
              } pixels. Images will be resized and converted to WebP.`,
            }}
          />
          {splashScreenDetails?.imageUrl && !image && (
            <Box
              component="img"
              src={splashScreenDetails?.imageUrl}
              alt="Image Preview"
              className={styles.preview}
              style={{ width: "50%", marginTop: 20 }}
            />
          )}
        </Box>
      </Box>
      <Box sx={{ mt: 3 }}>
        <TargetedUsersUpload
          value={splashScreenDetails.targetedUsers}
          onChange={(userIds) =>
            setSplashScreenDetails((prev) => ({
              ...prev,
              targetedUsers: userIds,
            }))
          }
        />
      </Box>
      <Box sx={{ mt: 3 }}>
        <BulkTranslationUpload
          assetType="SPLASH_SCREEN"
          assetId={id !== "create" ? id : undefined}
          fields={[
            { label: "Title", fieldName: "titleTrans" },
            { label: "Body", fieldName: "bodyTrans" },
          ]}
          {...(id === "create"
            ? { deferUpload: true, onFileChange: setTranslationFile }
            : { onSuccess: fetchSplashScreenInfo })}
        />
      </Box>
      <ViewLanguageModal
        isMultiline={true}
        label="Select Language"
        isOpen={isOpen}
        close={close}
        onChange={handleChange}
        handleOnSubmit={handleAddTranslation}
        actionHandlers={{
          handleLangEditClick: handleLangEditClick,
          handleLangDeleteClick: handleLangDeleteClick,
        }}
        modalData={modalData}
        tableColumns={[
          {
            id: "lang",
            label: "Language",
            alignHead: "center",
            alignBody: "center",
          },
          {
            id: "inputValue",
            label: "Translation",
            alignBody: "center",
            alignHead: "center",
          },
          {
            id: "actions",
            label: "Actions",
            alignHead: "center",
            alignBody: "center",
          },
        ]}
      />
    </ContentShell>
  );
};

export default SplashScreenEditor;
